import {
  SupportSessionDrawer,
  SupportSessionsTable,
  useSupportSessionMutations,
  useSupportSessions,
  useTenant,
  type SupportSession,
} from "@how/app-kit";
import { Button, Card, Group } from "@mantine/core";
import { useState } from "react";
import { ConfirmActionModal, PageHeader } from "@how/ui";

/**
 * What DCX Logic support may do in this church: approve a pending read-and-write request, revoke access, and see the
 * exact scope of every session. Read-only sessions start without approval and are listed for transparency.
 */
export function SupportAccessPage() {
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : "";
  const sessions = useSupportSessions({ tenantId });
  const mutations = useSupportSessionMutations();
  const [selected, setSelected] = useState<SupportSession | null>(null);
  const [revoking, setRevoking] = useState<SupportSession | null>(null);
  const current =
    selected === null ? null : (sessions.data?.find((s) => s.id === selected.id) ?? selected);
  return (
    <>
      <PageHeader
        title="Support access"
        description="DCX Logic support can only act inside a time-bound session. Approve read-and-write requests here, and revoke any session at any time."
      />
      <Card>
        <SupportSessionsTable
          sessions={sessions.data}
          isPending={sessions.isPending}
          error={sessions.error}
          onRetry={() => {
            void sessions.refetch();
          }}
          onSelect={setSelected}
          emptyTitle="No support sessions"
          emptyDescription="Nobody from DCX Logic has requested access to your church."
        />
      </Card>
      <SupportSessionDrawer
        session={current}
        onClose={() => {
          setSelected(null);
        }}
        actions={
          current === null ? null : (
            <Group gap="sm">
              {current.status === "requested" && current.accessLevel === "read_write" ? (
                <Button
                  loading={mutations.approve.isPending}
                  onClick={() => {
                    mutations.approve.mutate(current.id);
                  }}
                >
                  Approve
                </Button>
              ) : null}
              {current.status === "requested" || current.status === "active" ? (
                <Button
                  variant="light"
                  color="red"
                  onClick={() => {
                    setRevoking(current);
                  }}
                >
                  Revoke
                </Button>
              ) : null}
            </Group>
          )
        }
      />
      <ConfirmActionModal
        opened={revoking !== null}
        onClose={() => {
          setRevoking(null);
        }}
        title="Revoke this support session?"
        message="DCX Logic support loses access to your church immediately. The revocation is written to the audit log."
        confirmLabel="Revoke"
        danger
        loading={mutations.end.isPending}
        onConfirm={() => {
          if (revoking === null) return;
          mutations.end.mutate(revoking.id, {
            onSuccess: () => {
              setRevoking(null);
              setSelected(null);
            },
          });
        }}
      />
    </>
  );
}
